import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AngularFireAuth } from '@angular/fire/auth';
import { map, switchMap, take } from 'rxjs/operators';
import { Observable, of, from } from 'rxjs';
import { getUserPermissionValue, RoleType, User } from '@webtronic-labs/contracts-proflyt';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { FirebasePersistence } from '../core/enums';
import { Store, select } from '@ngrx/store';
import { selectAuthState } from '../store-auth/selectors/auth.selectors';
import { IAuthState } from '../store-auth/reducers/auth-state.reducer';
import { SetAuthStatus, ResetAuthState } from '../store-auth/actions/auth.actions';
import { AuthApiService } from './auth-api.service';
import { ResetFilters } from '../../super-admin/store-admin/actions/admin.actions';
import { ResetSquadronStore } from '../../squadron/store/actions/squadron.actions';
import firebase from 'firebase/app';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public firebaseUser$: Observable<firebase.User>;

  constructor(
    private angularFireAuth: AngularFireAuth,
    private store: Store,
    private http: HttpClient,
    private router: Router,
    private authApiService: AuthApiService
  ) {
    this.firebaseUser$ = this.angularFireAuth.authState;
  }

  public login(email: string, password: string) {
    this.angularFireAuth.setPersistence(FirebasePersistence.Local);

    return from(this.angularFireAuth.signInWithEmailAndPassword(email, password)).pipe(
      switchMap((user: firebase.auth.UserCredential) => {
        return this.setProflytUserInStore(user.user);
      })
    );
  }

  public logout(shouldNotRedirect?: boolean, areTokensAlreadyRevoked = false) {
    this.angularFireAuth.signOut();

    // Remove refresh tokens on API
    if (!areTokensAlreadyRevoked) {
      this.http
        .get(environment.apiUrl + '/auth/logout')
        .pipe(take(1))
        .subscribe();
    }

    this.store.dispatch(ResetAuthState());
    this.store.dispatch(ResetFilters());
    this.store.dispatch(ResetSquadronStore());

    if (shouldNotRedirect) {
      return;
    }

    this.router.navigate(['/auth']);
  }

  public isAuth() {
    return this.angularFireAuth.authState.pipe(
      switchMap((firebaseUser: firebase.User) => {
        return this.setProflytUserInStore(firebaseUser).pipe(
          map(() => {
            return firebaseUser;
          })
        );
      })
    );
  }

  /**
   * This method will return a merged observable of firebase auth
   * and proflyt user, if the user is logged, it will return the User object,
   * if not, will return null
   */
  public getProflytUser(): Observable<User> {
    return this.angularFireAuth.authState.pipe(
      switchMap((firebaseUser: firebase.User) => {
        return this.setProflytUserInStore(firebaseUser);
      })
    );
  }

  public resetPassword(userEmail: string): Observable<void> {
    const actionCodeSettings = {
      url: environment.clientUrl,
      handleCodeInApp: false,
    };

    return from(this.angularFireAuth.sendPasswordResetEmail(userEmail.trim(), actionCodeSettings));
  }

  /* Change login user password. */
  public changePassword(oldPassword: string, newPassword: string): Observable<void> {
    return from(this.angularFireAuth.currentUser).pipe(
      take(1),
      switchMap((user: firebase.User) => {
        const credentials = firebase.auth.EmailAuthProvider.credential(user.email, oldPassword);

        return this.validateUserCurrentPassword(credentials, newPassword, user);
      })
    );
  }

  public confirmPasswordReset(code: string, newPassword: string) {
    return from(this.angularFireAuth.confirmPasswordReset(code, newPassword));
  }

  // Verify if active user can update, delete or change role to an other user.
  public verifyUserPermissions(activeUserRoleType: RoleType, roleType: RoleType) {
    const activeUserRole: number = getUserPermissionValue(activeUserRoleType);
    const role: number = getUserPermissionValue(roleType);

    return activeUserRole >= role;
  }

  /**
   * This function will check the auth user, check the store,
   * request user info, and create local user object, only once per login/logout
   */
  private setProflytUserInStore(firebaseUser: firebase.User): Observable<User> {
    return this.store.pipe(
      select(selectAuthState),
      switchMap((authState: IAuthState) => {
        if (firebaseUser && authState.user) {
          // Logged in, no need for anything
          return of(authState.user);
        }

        if (!firebaseUser) {
          // Not logged in
          if (authState.isLoggedIn) {
            this.store.dispatch(
              SetAuthStatus({
                isLoggedIn: false,
                user: null,
              })
            );
          }

          return of(null);
        }

        // Logged in, and no state in store
        return this.authApiService.getCurrentUserInfo().pipe(
          take(1),
          switchMap((userInfo: User) => {
            if (userInfo?.isPublicUser) {
              const publicUser: Partial<User> = {
                isPublicUser: true,
                _id: firebaseUser.uid,
              };
              this.store.dispatch(
                SetAuthStatus({
                  isLoggedIn: true,
                  user: publicUser as User,
                })
              );

              return of(publicUser);
            }

            const newUser = new User(userInfo);
            this.store.dispatch(
              SetAuthStatus({
                isLoggedIn: true,
                user: new User(userInfo),
              })
            );

            return of(newUser);
          })
        );
      }),
      take(1)
    );
  }

  // Re-authenticating the user to verify the current password, and update password.
  private validateUserCurrentPassword(
    credentials: firebase.auth.AuthCredential,
    password: string,
    user: firebase.User
  ) {
    return from(user.reauthenticateWithCredential(credentials)).pipe(
      take(1),
      switchMap(() => from(user.updatePassword(password)))
    );
  }
}
